// [data-health-frontend-v2]
// dataHealthCopy.ts
// Central mapping from backend provider-registry status values to user-facing
// labels, messages, severity, and badge tone. This is the single source of
// truth for the new data-health contract introduced when the budget-aware
// provider registry was wired (Tavily, Exa, Twelve Data, fallback chain).
//
// The backend emits one of these status strings on the new
// /api/v1/market-pulse/data-health endpoint and on each Council `data_quality`
// component (quote_status, options_status, vix_status, calendar_status,
// crypto_news_status). The frontend must never display "Partial Data" or
// raw "Unavailable" without the more specific reason when one of these
// statuses is present.

export type DataHealthStatus =
  | "LIVE"
  | "FALLBACK_ACTIVE"
  | "CACHE_BY_QUOTA_POLICY"
  | "DELAYED_SOURCE"
  | "CREDENTIAL_MISSING"
  | "OPTIONS_PROVIDER_NOT_CONFIGURED"
  | "PROXY_ACTIVE"
  | "NOT_APPLICABLE"
  | "STALE"
  | "LIMITED"
  | "LIMITED_FALLBACK_CONTEXT"
  | "QUOTA_LIMITED"
  | "PROVIDER_ERROR"
  | "UNSUPPORTED"
  | "UNAVAILABLE";

export type Severity = "success" | "info" | "warning" | "error" | "neutral";

export interface DataHealthCopy {
  label: string;
  message: string;
  severity: Severity;
  /** UI badge tone: maps to the existing FreshnessLevel palette so the
   * component does not need new colors to render this. */
  badgeTone: "live" | "partial" | "stale" | "degraded" | "unavailable";
}

const COPY: Record<DataHealthStatus, DataHealthCopy> = {
  LIVE: {
    label: "Live",
    message: "Fresh provider data is available.",
    severity: "success",
    badgeTone: "live",
  },
  FALLBACK_ACTIVE: {
    label: "Fallback Active",
    message:
      "Primary source was limited, so Adversyn used a verified fallback provider.",
    severity: "info",
    badgeTone: "partial",
  },
  CACHE_BY_QUOTA_POLICY: {
    label: "Cached by quota policy",
    message:
      "Using recently cached data to preserve free-tier API limits.",
    severity: "info",
    badgeTone: "partial",
  },
  DELAYED_SOURCE: {
    label: "Delayed source",
    message:
      "This provider may be delayed under the current access tier.",
    severity: "warning",
    badgeTone: "stale",
  },
  CREDENTIAL_MISSING: {
    label: "Credential missing",
    message:
      "Provider is configured but the API key is not available.",
    severity: "warning",
    badgeTone: "stale",
  },
  PROXY_ACTIVE: {
    label: "Proxy Active",
    message:
      "True provider not available; using a proxy series — direction is informative, magnitude is not exact.",
    severity: "info",
    badgeTone: "partial",
  },
  OPTIONS_PROVIDER_NOT_CONFIGURED: {
    label: "Options provider not configured",
    message: "Options flow is not part of the current data stack.",
    severity: "info",
    badgeTone: "partial",
  },
  NOT_APPLICABLE: {
    label: "Not applicable",
    message: "This data type is not required for this asset class.",
    severity: "neutral",
    badgeTone: "live",
  },
  STALE: {
    label: "Stale",
    message:
      "Latest verified data is older than the freshness target.",
    severity: "warning",
    badgeTone: "stale",
  },
  LIMITED: {
    label: "Limited",
    message:
      "Some components are missing or using fallback sources.",
    severity: "warning",
    badgeTone: "stale",
  },
  LIMITED_FALLBACK_CONTEXT: {
    // 2026-05-21 backend Market Pulse cleanup renamed the partial-state
    // regime label from PARTIAL_DATA to LIMITED_FALLBACK_CONTEXT. Core
    // providers are live; non-core data is being served via verified
    // fallback / quota-protected cache / proxy.
    label: "Limited fallback context",
    message:
      "Core providers are live; some non-core sources are on verified fallback or proxy.",
    severity: "info",
    badgeTone: "partial",
  },
  QUOTA_LIMITED: {
    label: "Provider quota protected",
    message:
      "Provider quota is exhausted for this cycle; reusing recent cache.",
    severity: "info",
    badgeTone: "partial",
  },
  PROVIDER_ERROR: {
    label: "Provider error",
    message: "Provider returned an error; fallback attempted.",
    severity: "warning",
    badgeTone: "degraded",
  },
  UNSUPPORTED: {
    label: "Unsupported",
    message:
      "Symbol or asset class is not supported by the configured providers.",
    severity: "neutral",
    badgeTone: "partial",
  },
  UNAVAILABLE: {
    label: "Unavailable",
    message:
      "No configured provider returned usable data after fallback attempts.",
    severity: "error",
    badgeTone: "unavailable",
  },
};

const ALIAS: Record<string, DataHealthStatus> = {
  // Lower-case + legacy backend forms map to the canonical status.
  live: "LIVE",
  fallback_active: "FALLBACK_ACTIVE",
  cache_by_quota_policy: "CACHE_BY_QUOTA_POLICY",
  delayed_source: "DELAYED_SOURCE",
  credential_missing: "CREDENTIAL_MISSING",
  options_provider_not_configured: "OPTIONS_PROVIDER_NOT_CONFIGURED",
  proxy_active: "PROXY_ACTIVE",
  proxy: "PROXY_ACTIVE",
  not_applicable: "NOT_APPLICABLE",
  stale: "STALE",
  limited: "LIMITED",
  limited_fallback_context: "LIMITED_FALLBACK_CONTEXT",
  quota_limited: "QUOTA_LIMITED",
  provider_error: "PROVIDER_ERROR",
  unsupported: "UNSUPPORTED",
  unavailable: "UNAVAILABLE",
  // Backend "partial" / "degraded" legacy → map to LIMITED so we drop
  // the standalone "Partial Data" badge while keeping signal.
  partial: "LIMITED",
  degraded: "LIMITED",
  // 2026-05-21 back-compat: the previous backend Market Pulse cleanup
  // also emitted PARTIAL_DATA as a market_regime.label. Map it to the
  // new LIMITED_FALLBACK_CONTEXT entry so the UI keeps rendering the
  // partial-state badge consistently during the migration window.
  partial_data: "LIMITED_FALLBACK_CONTEXT",
  pass: "LIVE",
  ok: "LIVE",
  fresh: "LIVE",
  healthy: "LIVE",
};

/**
 * Resolve a backend status string (any casing) to the canonical entry.
 * Returns LIMITED for unknown values rather than UNAVAILABLE — that keeps
 * the explanation honest instead of falsely claiming "no data."
 */
export function resolveDataHealth(raw: unknown): DataHealthCopy {
  const s = String(raw ?? "").trim();
  if (!s) return COPY.UNAVAILABLE;
  const upper = s.toUpperCase() as DataHealthStatus;
  if (upper in COPY) return COPY[upper];
  const lower = s.toLowerCase();
  const alias = ALIAS[lower];
  if (alias) return COPY[alias];
  return COPY.LIMITED;
}

/**
 * Compose a per-component label like "Equities: Fallback Active" without
 * losing the legacy compact format that the existing Data Health card uses.
 */
export function componentHealthLabel(
  displayName: string,
  status: unknown,
): string {
  const copy = resolveDataHealth(status);
  return `${displayName}: ${copy.label}`;
}

/**
 * Build a one-sentence row description for a Data Health row.
 * Always includes provider and fallback info when present.
 */
export function buildRowDescription(args: {
  status: unknown;
  provider?: string | null;
  fallbackUsed?: boolean | null;
  lastUpdatedHuman?: string | null;
  missingComponents?: string[] | null;
  quotaProtectionActive?: boolean | null;
}): string {
  const { status, provider, fallbackUsed, lastUpdatedHuman, missingComponents, quotaProtectionActive } = args;
  const copy = resolveDataHealth(status);
  const parts: string[] = [copy.message];
  if (provider) {
    parts.push(`Provider: ${provider}.`);
  }
  if (fallbackUsed) {
    parts.push("Fallback in use.");
  }
  if (quotaProtectionActive) {
    parts.push("Free-tier quota protection active.");
  }
  if (missingComponents && missingComponents.length > 0) {
    parts.push(`Missing: ${missingComponents.slice(0, 4).join(", ")}.`);
  }
  if (lastUpdatedHuman) {
    parts.push(`Updated ${lastUpdatedHuman}.`);
  }
  return parts.join(" ");
}

/**
 * Helper: explain a Strategy Impact summary from a component status map.
 * Replaces the legacy hard-coded "operating with partial data" line.
 */
export function buildStrategyImpactCopy(args: {
  overall: string;
  componentStatuses: Record<string, unknown>;
  permissionLabel: string;
}): string {
  const { overall, componentStatuses, permissionLabel } = args;
  const overallCopy = resolveDataHealth(overall);
  if (overallCopy.label === "Live") {
    return "Market intelligence is healthy. Strategy rules are running at full confidence.";
  }

  // Bucket components by what's happening.
  const fallback: string[] = [];
  const cached: string[] = [];
  const notConfigured: string[] = [];
  const delayed: string[] = [];
  const unavailable: string[] = [];
  const stale: string[] = [];

  for (const [name, raw] of Object.entries(componentStatuses)) {
    const c = resolveDataHealth(raw);
    const label = name.charAt(0).toUpperCase() + name.slice(1);
    switch (c.label) {
      case "Fallback Active":
        fallback.push(label);
        break;
      case "Cached by quota policy":
      case "Provider quota protected":
        cached.push(label);
        break;
      case "Options provider not configured":
      case "Credential missing":
        notConfigured.push(label);
        break;
      case "Delayed source":
        delayed.push(label);
        break;
      case "Unavailable":
        unavailable.push(label);
        break;
      case "Stale":
      case "Limited":
        stale.push(label);
        break;
      default:
        break;
    }
  }

  const sentences: string[] = [];
  if (fallback.length) sentences.push(`Fallback active for ${fallback.join(", ")}.`);
  if (cached.length) sentences.push(`Quota-protected cache active for ${cached.join(", ")}.`);
  if (notConfigured.length) sentences.push(`Provider not configured for ${notConfigured.join(", ")}.`);
  if (delayed.length) sentences.push(`Delayed source in use for ${delayed.join(", ")}.`);
  if (stale.length) sentences.push(`Stale data for ${stale.join(", ")}.`);
  if (unavailable.length) sentences.push(`No data after fallback for ${unavailable.join(", ")}.`);

  sentences.push(
    `${permissionLabel}. Agents may use verified fallback data but cannot expand risk when required sources are missing.`,
  );

  return sentences.join(" ");
}
